import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { createExamsWizard, createExamsForAllClassLevels } from '../services/examService';

interface SimpleExamWizardProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const SimpleExamWizard: React.FC<SimpleExamWizardProps> = ({ open, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [createForAllClassLevels, setCreateForAllClassLevels] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    class_level: '',
    exam_date: new Date().toISOString().split('T')[0],
    duration_minutes: 60,
    total_marks: 100,
    passing_marks: 50,
    exam_type: 'midterm',
    is_active: true,
    school_id: user?.school_id || 0
  });

  const classLevels = [
    'Pre-Primary 1', 'Pre-Primary 2',
    'Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5',
    'Grade 6', 'Grade 7', 'Grade 8',
    'Grade 9', 'Grade 10', 'Grade 11', 'Grade 12'
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (!createForAllClassLevels && !formData.class_level) {
      setError('Please select a class level or choose "All Class Levels"');
      setLoading(false);
      return;
    }

    try {
      if (createForAllClassLevels) {
        // Create exams for all class levels using the new endpoint
        await createExamsForAllClassLevels({
          ...formData,
          exam_date: new Date(formData.exam_date).toISOString()
        });
        setSuccess(`Successfully created exams for all ${classLevels.length} class levels!`);
      } else {
        // Create exam for single class level
        await createExamsWizard({
          ...formData,
          exam_date: new Date(formData.exam_date).toISOString()
        });
        setSuccess(`Successfully created exam for ${formData.class_level}!`);
      }
      
      setTimeout(() => {
        onSuccess();
        onClose();
        resetForm();
      }, 2000);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to create exams');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      class_level: '',
      exam_date: new Date().toISOString().split('T')[0],
      duration_minutes: 60,
      total_marks: 100,
      passing_marks: 50,
      exam_type: 'midterm',
      is_active: true,
      school_id: user?.school_id || 0
    });
    setCreateForAllClassLevels(false);
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      setError('');
      setSuccess('');
      resetForm();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h5">Exam Creator Wizard</Typography>
        <Typography variant="body2" color="text.secondary">
          Create exams for class levels
        </Typography>
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Exam Title"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="e.g., First Term Midterm"
              required
            />
            
            <TextField
              fullWidth
              label="Description (Optional)"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              multiline
              rows={3}
              placeholder="Brief description of the exam..."
            />
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={createForAllClassLevels}
                  onChange={(e) => {
                    setCreateForAllClassLevels(e.target.checked);
                    if (e.target.checked) {
                      setFormData(prev => ({ ...prev, class_level: '' }));
                    }
                  }}
                />
              }
              label="Create for all class levels"
            />
            
            {!createForAllClassLevels && (
              <FormControl fullWidth>
                <InputLabel>Class Level</InputLabel>
                <Select
                  value={formData.class_level}
                  onChange={(e) => setFormData(prev => ({ ...prev, class_level: e.target.value }))}
                  label="Class Level"
                  required
                >
                  {classLevels.map((level) => (
                    <MenuItem key={level} value={level}>
                      {level}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            
            <FormControl fullWidth>
              <InputLabel>Exam Type</InputLabel>
              <Select
                value={formData.exam_type}
                onChange={(e) => setFormData(prev => ({ ...prev, exam_type: e.target.value }))}
                label="Exam Type"
              >
                <MenuItem value="midterm">Midterm Exam</MenuItem>
                <MenuItem value="final">Final Exam</MenuItem>
                <MenuItem value="quiz">Quiz</MenuItem>
                <MenuItem value="assignment">Assignment</MenuItem>
                <MenuItem value="test">Test</MenuItem>
                <MenuItem value="project">Project</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              label="Exam Date"
              type="date"
              value={formData.exam_date}
              onChange={(e) => setFormData(prev => ({ ...prev, exam_date: e.target.value }))}
              required
              InputLabelProps={{ shrink: true }}
            />
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Duration (minutes)"
                type="number"
                value={formData.duration_minutes}
                onChange={(e) => setFormData(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) }))}
                inputProps={{ min: 1 }}
                required
              />
              
              <TextField
                fullWidth
                label="Total Marks"
                type="number"
                value={formData.total_marks}
                onChange={(e) => setFormData(prev => ({ ...prev, total_marks: parseFloat(e.target.value) }))}
                inputProps={{ min: 1, step: 0.1 }}
                required
              />
            </Box>
            
            <TextField
              fullWidth
              label="Passing Marks"
              type="number"
              value={formData.passing_marks}
              onChange={(e) => setFormData(prev => ({ ...prev, passing_marks: parseFloat(e.target.value) }))}
              inputProps={{ min: 0, max: formData.total_marks, step: 0.1 }}
              required
            />
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !formData.title}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Creating...' : createForAllClassLevels ? `Create for All ${classLevels.length} Class Levels` : 'Create Exam'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default SimpleExamWizard; 